/**
 * Assembling one OpenAI streamed turn from its chunks (18.08.2026).
 *
 * Written against a REAL captured stream, not from documentation, because the
 * shape has one property that matters and is easy to assume wrongly:
 *
 *   **OpenAI has no per-call completion signal.** Anthropic closes each tool
 *   call with `content_block_stop`, so a call can be emitted the moment its own
 *   block ends. OpenAI does not: the id and function name arrive on the FIRST
 *   fragment of a call, the arguments arrive as further fragments keyed by
 *   `index`, and nothing says "this one is finished" until the chunk carrying
 *   `finish_reason`. Emitting before that means emitting a call whose arguments
 *   may still be arriving — `terminal_run_command` with half a command.
 *
 * So every call is held until finish_reason, then emitted together. Observed
 * shape, trimmed, from a two-call turn:
 *
 *   delta.tool_calls[{index:0, id:'call_06vQZ', function:{name:'…', arguments:''}}]
 *   delta.tool_calls[{index:0, function:{arguments:'{"co'}}]
 *   delta.tool_calls[{index:0, function:{arguments:'nn_id'}}]
 *   …
 *   delta.tool_calls[{index:1, id:'call_z1Srw', function:{name:'…', arguments:''}}]
 *   …
 *   finish_reason:'tool_calls'
 *
 * Note the second and later fragments carry NO id and NO name — keying by
 * `index` is the only thing that keeps two concurrent calls apart.
 */

/** A fresh accumulator for one turn. */
export function createOpenAiAssembly() {
  return {
    /** index → { id, name, args } for calls still arriving. */
    pending: new Map(),
    stopReason: null,
    usage: null,
  };
}

/**
 * Feed one `data:` payload in; get back what the caller should emit now.
 *
 * Returns `{ text?, toolUses?, usage?, stopReason?, done? }`. `toolUses` is an
 * ARRAY because they all land at once — there is no point at which exactly one
 * becomes ready.
 */
export function pushOpenAiEvent(state, payload) {
  if (!payload) return {};
  if (payload === '[DONE]') {
    return { done: true, stopReason: state.stopReason || 'end_turn' };
  }

  let chunk;
  try {
    chunk = JSON.parse(payload);
  } catch {
    // One malformed frame must not end a turn that is otherwise fine.
    return {};
  }

  const out = {};

  // Usage arrives only when stream_options.include_usage was requested; when it
  // does, it is on a chunk of its own with an empty choices array.
  if (chunk.usage) {
    state.usage = {
      input_tokens: chunk.usage.prompt_tokens || 0,
      output_tokens: chunk.usage.completion_tokens || 0,
    };
    out.usage = state.usage;
  }

  const choice = (chunk.choices || [])[0];
  if (!choice) return out;

  const delta = choice.delta || {};
  if (delta.content) out.text = delta.content;

  for (const call of delta.tool_calls || []) {
    const index = call.index ?? 0;
    const existing = state.pending.get(index) || { id: null, name: null, args: '' };
    // id/name only on the first fragment; ?? keeps a later null from erasing them.
    existing.id = call.id ?? existing.id;
    existing.name = call.function?.name ?? existing.name;
    existing.args += call.function?.arguments ?? '';
    state.pending.set(index, existing);
  }

  if (choice.finish_reason) {
    state.stopReason = choice.finish_reason === 'tool_calls'
      ? 'tool_use'
      : choice.finish_reason === 'length' ? 'max_tokens' : 'end_turn';
    // The only moment every call is known to be complete.
    if (state.pending.size) {
      out.toolUses = [...state.pending.entries()]
        .sort(([a], [b]) => a - b)
        .map(([, call]) => finishCall(call));
      state.pending.clear();
    }
    out.stopReason = state.stopReason;
  }

  return out;
}

/**
 * An accumulated call in the shape the panel dispatches.
 *
 * Empty arguments are `{}`, not a failure: a tool with no parameters streams a
 * single fragment of `''`. A non-empty string that will not parse means the
 * stream was cut mid-call, which is reported rather than guessed at — half of a
 * JSON object is how a truncated command gets run.
 */
function finishCall(call) {
  const raw = (call.args || '').trim();
  if (!raw) return { id: call.id, name: call.name, input: {} };
  try {
    return { id: call.id, name: call.name, input: JSON.parse(raw) };
  } catch {
    return { id: call.id, name: call.name, input: {}, incomplete: true };
  }
}

/**
 * Calls still accumulating when the stream ended.
 *
 * Non-empty means the connection dropped before `finish_reason`, so nothing was
 * ever emitted — which is correct, and the caller should say the turn was cut
 * off rather than run a partial call.
 */
export function unfinishedOpenAiCalls(state) {
  return [...state.pending.values()].map((c) => ({ id: c.id, name: c.name }));
}
